import React, {useContext, useState} from "react";
import {Button, Input, Select} from "antd";
import {
    FileProtectOutlined,
    UploadOutlined
} from "@ant-design/icons";
import {IPolicyViewProps} from "./types/policies.types";
import UserLoginContext from "../../context/userLoginContext";
import PolicyCreateModal from "./utils/policy-create-modal";
import resources from "../../resources";
import ScrollPagination from "../common/scroll-pagination/scroll-pagination";
import DocumentCard from "../common/document-card/document-card";
import {IPolicyResponse} from "../../domains/models/policy";
import {IFile} from "../common/document-card/types/document-card.types";
import {getImageUrl} from "../../utility/utility";
import EmptyData from "../common/empty-data/empty-data";
import PageHeader from "../common/page-header/page-header";
import SearchCriteria from "../common/search-criteria/search-criteria";
import {Policy_Status} from "../../constant/constant";
import {searchCriteria} from "./utils/utils";

const PolicyView = (props: IPolicyViewProps) => {
    const {user} = useContext(UserLoginContext);
    const isAdmin = user?.isAdmin;
    const tempResources = resources.policyPage;
    const [open, setOpen] = useState<boolean>(false);

    const policyDescription = isAdmin ? tempResources.adminEmptyPageTitle : tempResources.userEmptyPageTitle;

    function mapPolicyToFile(policy: IPolicyResponse): IFile {
        return {
            id: policy.id!,
            name: policy.title,
            date: policy.applicableFrom,
            size: policy.document.size ?? "",
            type: policy.document.documentType,
            description: policy.description ?? '',
            contentType: policy.document.contentType ?? 'application/pdf',
            fileCoverUrl: getImageUrl(policy.document, 'medium'),
            orgDocumentId: policy.document.id,
            status: policy.status
        }
    }

    return (
        <>
            <PageHeader
                title={'Company Policy'}
                description={'Browse workplace guidelines, rules, and compliance policies'}
                actions={props.policies?.length > 0 && isAdmin &&
                (<Button
                    type={'primary'}
                    icon={<UploadOutlined/>}
                    onClick={() => setOpen(true)}
                    className={'upload-policy-btn'}
                >
                    {tempResources.uploadPolicyButton}
                </Button>)
                }
            />
            {isAdmin &&
            <SearchCriteria
                title={"Search Policies"}
                onSearch={props.handlePolicySearch}
                searchCriteria={searchCriteria}
                showReset={true}
                onReset={props.handleReset}
                renderTopContent={({values, onChange}) => (
                    <>
                        <div>
                            <label className={"title"}>Title</label>
                            <Input
                                placeholder="Enter title"
                                value={values.title || ''}
                                onChange={(e) => onChange('title', e.target.value)}
                            />
                        </div>
                        <div>
                            <label className={"label"}>Select Status</label>
                            <Select
                                placeholder={"Select policy status"}
                                onChange={(val: any) => onChange('status', val)}
                                allowClear
                                value={values.status ?? values.status}
                                className={"label"}
                            >
                                {(Object.keys(Policy_Status) as Array<keyof typeof Policy_Status>).map((key) =>
                                    <Select.Option value={key} key={key}>
                                        {Policy_Status[key]}
                                    </Select.Option>
                                )}
                            </Select>
                        </div>
                    </>
                )}
            />}
            {
                props.policies?.length == 0 ?
                    <EmptyData icon={<FileProtectOutlined className={"empty-policy-page-icon"}/>}
                               title={"Oops! It's a bit empty here... 📋"}
                               subtitle={policyDescription}
                               button={
                                   isAdmin &&
                                   <Button onClick={() => setOpen(true)}>
                                       <UploadOutlined className={"upload-icon"}/>
                                       {tempResources.uploadFirstPolicyText}
                                   </Button>
                               }
                    /> :
                    <div className={"policy-page"}>
                        <ScrollPagination
                            isLoading={props.paginationControls.isLoading}
                            loadNext={props.paginationControls.loadNext}
                            loadPrevious={props.paginationControls.loadPrevious}
                            hasNext={props.paginationControls.hasNext}
                            hasPrevious={props.paginationControls.hasPrevious}
                            className="policy-body"
                        >
                            <div className="policy-grid">
                                {props.policies?.map((policy: IPolicyResponse) => {
                                    const file = mapPolicyToFile(policy);
                                    const showNotify = !policy.notifyEmployees;
                                    return (
                                        <DocumentCard
                                            key={policy.id}
                                            file={file}
                                            handleDeleteDoc={props.handleDeletedDoc}
                                            showDelete={false}
                                            showUnpublished={true}
                                            handleUnpublishPolicy={props.handleUnpublishPolicy}
                                            handlePublishPolicy={props.handlePublishPolicy}
                                            showPublished={true}
                                            showNotify={showNotify}
                                            handleNotifyPolicy={props.handleNotifyPolicy}
                                        />
                                    );
                                })}
                            </div>
                        </ScrollPagination>
                    </div>
            }

            <PolicyCreateModal
                open={open}
                setOpen={setOpen}
                onSubmit={props.onSubmit}
            />
        </>
    );
}

export default PolicyView;